import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DatePicker from "react-datepicker";
import Select from "react-select";

import {
    patchFinanceOpsJob,
    postFinanceOpsApplyPayment,
    submitHrpFundingPayment,
    submitHrpPremiumPayment,
} from "../../utils/apiUtils";
import { customStyles } from "../../utils/styleUtils";
import "./ApplyPaymentPanel.scss";

const PAYMENT_TYPE_OPTIONS = [
    { value: "Check", label: "Check" },
    { value: "EFT", label: "EFT" },
    { value: "Wire", label: "Wire" },
    { value: "ACH", label: "ACH" },
    { value: "Other", label: "Other" },
];

const toIsoDate = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const money = (n) => {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    return Number(n).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    });
};

/** Normalize HRP / SOAP / proxy errors for the UI. */
const formatHrpErrorMessage = (raw) => {
    if (raw == null) return "Unknown HRP error";
    if (typeof raw === "object") {
        raw =
            raw.message ||
            raw.errorMessage ||
            raw.error?.message ||
            raw.error?.errorMessage ||
            JSON.stringify(raw);
    }
    let text = String(raw);
    try {
        const parsed = JSON.parse(text);
        if (parsed?.message) text = parsed.message;
        else if (parsed?.errorMessage) text = parsed.errorMessage;
        else if (parsed?.error != null) {
            text =
                typeof parsed.error === "string"
                    ? parsed.error
                    : parsed.error?.message ||
                      parsed.error?.errorMessage ||
                      JSON.stringify(parsed.error);
        }
    } catch {
        // not JSON
    }

    const faultMatch = text.match(/faultstring>([^<]+)/i);
    if (faultMatch?.[1]) text = faultMatch[1];

    const attrMatch = text.match(
        /([A-Za-z][\w.]*):\s*attribute:\s*'([^']+)'\s*:\s*([^\n]+)/,
    );
    if (attrMatch) {
        return `HRP rejected ${attrMatch[1]}.${attrMatch[2]}: ${attrMatch[3].trim()}`;
    }

    if (/IOMMandatoryAttributeConstraintException/i.test(text)) {
        return "HRP rejected the premium payment: a required field is missing (IOMMandatoryAttributeConstraintException). Check the HRP connector server log for the attribute name.";
    }

    const iomMatch = text.match(/IOM\w+Exception[^\n]*/);
    if (iomMatch) {
        return `HRP rejected the payment: ${iomMatch[0].replace(/\s+/g, " ").trim()}`;
    }

    const saxLines = [...text.matchAll(/SAXParseException:\s*([^\n\]]+)/g)].map((m) =>
        m[1].replace(/\s+/g, " ").trim(),
    );
    if (saxLines.length) {
        return `HRP rejected the payment payload: ${saxLines[0]}`;
    }
    if (/Validation failed/i.test(text)) {
        const oneOf = text.match(/One of '\{[^}]+\}' is expected[^.]*/);
        if (oneOf) return `HRP schema validation failed. ${oneOf[0]}.`;
        return "HRP schema validation failed for this payment payload.";
    }

    text = text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (text.length > 420) text = `${text.slice(0, 420)}…`;
    return text || "HRP payment submit failed";
};

const asArray = (value) => {
    if (value == null) return [];
    return Array.isArray(value) ? value : [value];
};

/** HRP may return paymentResponse as an object or an array. */
const extractHrpPaymentOutcome = (hrpResult) => {
    const responses = asArray(hrpResult?.paymentResponse);
    const lineErrors = responses.flatMap((r) => asArray(r?.errors?.error || r?.errors));
    const topErrors = asArray(hrpResult?.errors?.error || hrpResult?.errors);
    const allErrors = [...lineErrors, ...topErrors];
    const lineStatus = String(
        hrpResult?.status || responses[0]?.status || "",
    ).toUpperCase();
    const failed =
        lineStatus.includes("FAIL") ||
        lineStatus.includes("ERROR") ||
        allErrors.length > 0;
    const firstErr = allErrors[0];
    // Prefer message + traceInfo so IOM attribute names surface when present.
    const errMsg =
        (typeof firstErr === "string"
            ? firstErr
            : [firstErr?.message || firstErr?.errorMessage, firstErr?.traceInfo]
                  .filter(Boolean)
                  .join("\n")) ||
        `HRP returned status ${lineStatus || "FAILURE"}`;
    return { responses, failed, errMsg, lineStatus };
};

/**
 * Employer inbound payment apply (funding request or premium bill).
 *
 * @param {'funding'|'premium'} kind
 * @param {object} context — account + FR or bill identifiers / defaults
 */
const ApplyPaymentPanel = ({ kind, context, onClose, onApplied, variant = "inline" }) => {
    const isDrawer = variant === "drawer";
    const queryClient = useQueryClient();
    const remaining = Number(context?.amountDue);
    const defaultAmount =
        Number.isFinite(remaining) && remaining > 0 ? String(remaining) : "";

    const [amount, setAmount] = useState(defaultAmount);
    const [paymentNumber, setPaymentNumber] = useState("");
    const [paymentDate, setPaymentDate] = useState(() => new Date());
    const [receiptDate, setReceiptDate] = useState(() => new Date());
    const [paymentType, setPaymentType] = useState(PAYMENT_TYPE_OPTIONS[0]);
    const [comments, setComments] = useState("");
    const [resultMsg, setResultMsg] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);

    const title = kind === "premium" ? "Apply premium payment" : "Apply funding payment";
    const hint =
        kind === "premium"
            ? "Posts employer cash to this premium bill in HRP (submitPremiumPayment)."
            : "Posts employer cash to this funding request in HRP (submitFundingPayment).";

    const fields = useMemo(() => {
        const base = {
            paymentAmount: amount,
            paymentNumber: paymentNumber || undefined,
            paymentDate: toIsoDate(paymentDate),
            receiptDate: toIsoDate(receiptDate),
            paymentType: paymentType?.value || "Check",
            comments: comments || undefined,
            accountKey: context?.accountKey,
            accountHccId: context?.accountHccId,
            accountName: context?.accountName,
        };
        if (kind === "funding") {
            return {
                ...base,
                fundingRequestId: context?.fundingRequestId ?? context?.key,
                paymentCycleId: context?.paymentCycleId || context?.cycleKey,
            };
        }
        return {
            ...base,
            billId: context?.billId ?? context?.key,
            benefitPlanId: context?.benefitPlanId || context?.planHccId,
            planHccId: context?.planHccId,
            billingCategoryName: context?.billingCategoryName,
            billingCategoryCode: context?.billingCategoryCode,
            coverageStartDate: context?.coverageStartDate || context?.billedPeriodStartDate,
            coverageEndDate: context?.coverageEndDate || context?.billedPeriodEndDate,
            billedPeriodStartDate: context?.billedPeriodStartDate,
            billedPeriodEndDate: context?.billedPeriodEndDate,
        };
    }, [kind, amount, paymentNumber, paymentDate, receiptDate, paymentType, comments, context]);

    const mutation = useMutation({
        mutationFn: async () => {
            setErrorMsg(null);
            setResultMsg(null);
            const prepared = await postFinanceOpsApplyPayment({ kind, fields });
            const jobId = prepared?.job?.id;
            const endpoint = prepared?.hrp?.endpoint;
            const body = prepared?.hrp?.body;
            if (!jobId || !endpoint || !body) {
                throw new Error("Payment prepare response incomplete");
            }

            try {
                const hrpResult =
                    kind === "premium"
                        ? await submitHrpPremiumPayment(body)
                        : await submitHrpFundingPayment(body);

                const { responses, failed, errMsg } = extractHrpPaymentOutcome(hrpResult);

                if (failed) {
                    const message = formatHrpErrorMessage(errMsg);
                    await patchFinanceOpsJob(jobId, {
                        status: "failed",
                        error: message,
                        meta: { ...(prepared.job.meta || {}), hrpResult },
                    });
                    throw new Error(message);
                }

                const corr =
                    hrpResult?.paymentNumber ||
                    hrpResult?.transactionInformation?.transactionId ||
                    responses[0]?.premiumPaymentBatchId ||
                    responses[0]?.paymentNumber ||
                    null;

                await patchFinanceOpsJob(jobId, {
                    status: "succeeded",
                    hrpCorrelationId: corr != null ? String(corr) : null,
                    meta: { ...(prepared.job.meta || {}), hrpResult },
                });
                return { prepared, hrpResult };
            } catch (err) {
                const errMsg = formatHrpErrorMessage(err?.message || err);
                await patchFinanceOpsJob(jobId, {
                    status: "failed",
                    error: errMsg,
                }).catch(() => {});
                throw new Error(errMsg);
            }
        },
        onSuccess: async (result) => {
            queryClient.invalidateQueries({ queryKey: ["finance-ops-jobs"] });
            setErrorMsg(null);
            const paymentResponses = asArray(result?.hrpResult?.paymentResponse);
            const batchId = paymentResponses[0]?.premiumPaymentBatchId;
            const payNum =
                result?.hrpResult?.paymentNumber || paymentResponses[0]?.paymentNumber;
            const baseMsg = `Payment applied${payNum ? ` · ${payNum}` : ""}${
                batchId != null ? ` · batch ${batchId}` : ""
            }.`;
            setResultMsg(`${baseMsg} Updating totals…`);
            try {
                const syncStatus = await onApplied?.(result);
                if (syncStatus === "optimistic") {
                    setResultMsg(
                        `${baseMsg} Totals updated here — HRP warehouse may take a few minutes to catch up.`,
                    );
                } else {
                    setResultMsg(baseMsg);
                }
            } catch {
                setResultMsg(baseMsg);
            }
        },
        onError: (err) => {
            queryClient.invalidateQueries({ queryKey: ["finance-ops-jobs"] });
            setResultMsg(null);
            setErrorMsg(formatHrpErrorMessage(err?.message || err));
        },
    });

    return (
        <section
            className={`apply-payment${isDrawer ? " apply-payment--drawer" : ""}`}
            aria-label={title}
        >
            {isDrawer ? (
                <p className="apply-payment__lead">{hint}</p>
            ) : (
                <div className="apply-payment__head">
                    <div>
                        <h2>{title}</h2>
                        <p>{hint}</p>
                    </div>
                    {onClose ? (
                        <button type="button" className="apply-payment__link" onClick={onClose}>
                            Close
                        </button>
                    ) : null}
                </div>
            )}

            <div className="apply-payment__context">
                <span>
                    Account:{" "}
                    <strong>
                        {context?.accountName || context?.accountHccId || "—"}
                        {context?.accountHccId ? ` (${context.accountHccId})` : ""}
                    </strong>
                </span>
                <span>
                    {kind === "premium" ? "Bill" : "Funding request"}:{" "}
                    <strong>#{context?.key ?? context?.billId ?? context?.fundingRequestId}</strong>
                </span>
                {Number.isFinite(remaining) ? (
                    <span>
                        Amount due: <strong>{money(remaining)}</strong>
                    </span>
                ) : null}
            </div>
            <div className="apply-payment__form">
                <div className="apply-payment__field">
                    <label htmlFor="apply-pay-amount">Amount</label>
                    <input
                        id="apply-pay-amount"
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                    />
                </div>
                <div className="apply-payment__field">
                    <label htmlFor="apply-pay-number">Payment number</label>
                    <input
                        id="apply-pay-number"
                        type="text"
                        value={paymentNumber}
                        onChange={(e) => setPaymentNumber(e.target.value)}
                        placeholder="Optional — auto if blank"
                    />
                </div>
                <div className="apply-payment__field">
                    <label>Payment type</label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={PAYMENT_TYPE_OPTIONS}
                        value={paymentType}
                        onChange={(opt) => setPaymentType(opt || PAYMENT_TYPE_OPTIONS[0])}
                        isSearchable={false}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{
                            ...customStyles,
                            menuPortal: (base) => ({ ...base, zIndex: 300 }),
                        }}
                    />
                </div>
                <div className="apply-payment__field">
                    <label>Payment date</label>
                    <DatePicker
                        selected={paymentDate}
                        onChange={(d) => setPaymentDate(d || new Date())}
                        dateFormat="yyyy-MM-dd"
                        todayButton="Today"
                        className="date-picker"
                        wrapperClassName="date-picker-wrapper"
                        portalId="root"
                        popperPlacement="bottom-start"
                        popperClassName="date-picker-popper"
                        popperProps={{ strategy: "fixed" }}
                    />
                </div>
                <div className="apply-payment__field">
                    <label>Receipt date</label>
                    <DatePicker
                        selected={receiptDate}
                        onChange={(d) => setReceiptDate(d || new Date())}
                        dateFormat="yyyy-MM-dd"
                        todayButton="Today"
                        className="date-picker"
                        wrapperClassName="date-picker-wrapper"
                        portalId="root"
                        popperPlacement="bottom-start"
                        popperClassName="date-picker-popper"
                        popperProps={{ strategy: "fixed" }}
                    />
                </div>
                <div className="apply-payment__field apply-payment__field--wide">
                    <label htmlFor="apply-pay-comments">Comments</label>
                    <input
                        id="apply-pay-comments"
                        type="text"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Optional"
                    />
                </div>
            </div>

            <div className="apply-payment__actions">
                <button
                    type="button"
                    className="btn apply-payment__submit apply-payment__submit--pay"
                    disabled={mutation.isPending || !amount || Number(amount) <= 0}
                    onClick={() => mutation.mutate()}
                >
                    {mutation.isPending ? "Submitting…" : "Submit payment"}
                </button>
                {resultMsg ? <p className="apply-payment__ok">{resultMsg}</p> : null}
            </div>
            {errorMsg ? (
                <p className="danger apply-payment__error" role="alert">
                    {errorMsg}
                </p>
            ) : null}
        </section>
    );
};

export default ApplyPaymentPanel;
